import { useMemo } from 'react';

import { useQueryStudySharePublic } from 'features/api/hooks/useQueryStudySharePublic';
import { useParsedShareAccessToken } from 'features/auth';

export interface ShareSenderInfo {
  senderName?: string;
  senderEmail?: string;
  description?: string;
  isLoading: boolean;
  error?: string;
}

/**
 * Hook to get sender information for the current share.
 *
 * @returns Share sender information including name, email, description, loading state, and error
 *
 * @example
 * ```tsx
 * const { senderName, senderEmail, description, isLoading } = useShareSenderInfo();
 *
 * if (isLoading) return <div>Loading sender info...</div>;
 *
 * return (
 *   <ShareTopHeader
 *     senderName={senderName}
 *     senderEmail={senderEmail}
 *   />
 * );
 * ```
 *
 * @note This hook uses the extended StudySharePublicModel which includes
 * sender information fields (senderName, senderEmail, description).
 * The backend GetStudySharePublic endpoint has been updated to return
 * this additional information.
 */
export function useShareSenderInfo(): ShareSenderInfo {
  const shareToken = useParsedShareAccessToken(false);
  const { data: shareData, isPending, error } = useQueryStudySharePublic(shareToken?.shareLinkId);

  return useMemo(() => {
    if (isPending) {
      return {
        isLoading: true,
      };
    }

    if (error) {
      return {
        isLoading: false,
        error: 'Failed to load sender information',
      };
    }

    if (!shareData) {
      return {
        isLoading: false,
        error: 'Share not found',
      };
    }

    return {
      senderName: shareData.senderName,
      senderEmail: shareData.senderEmail,
      description: shareData.description,
      isLoading: false,
    };
  }, [shareData, isPending, error]);
}
